import React, { memo, useCallback } from 'react';
import { toast } from 'react-toastify';

import { Modal } from 'antd';

import Alert from 'components/Alert';
import { Formik, Form } from 'formik';
import type { FormikProps } from 'formik';
import { functions } from 'helpers';
import type { IAffiliate } from 'v2/interfaces/affiliate';
import affiliateService from 'v2/services/affiliate';

import FormCommision from './FormCommision';
import type { IFormCommision } from './interfaces';
import validationSchema from './validations';

interface IProps {
  open: boolean;
  handleClose: (...args: any[]) => any;
  affiliate: IAffiliate | null;
}

const InitialValues = {
  active: false,
  type: 'P',
  value: ''
};

const CustomCommissionForm = ({ open, handleClose, affiliate }: IProps) => {
  const onSubmit = useCallback(
    async (values: IFormCommision, { setSubmitting }: FormikProps<IFormCommision>) => {
      if (!affiliate) {
        return;
      }

      setSubmitting(true);

      if (!values.active) {
        affiliateService.configCustomCommission(affiliate.id).subscribe(
          () => {
            toast.success('Comissão diferenciada configurada com sucesso!');
            handleClose();
          },
          () => {
            toast.error(
              'Erro ao configurar comissão, verifique se o valor da comissão não é maior que o valor do produto'
            );
          },
          () => {
            setSubmitting(false);
          }
        );
        return;
      }

      if (values.active && !values.value) {
        toast.error(`Insira ${values.type === 'P' ? 'a porcentagem' : 'o valor'}`);
        setSubmitting(false);
        return;
      }

      const cleanValue = Number.parseFloat(functions.clearMoneyMask(values.value));

      if (values.type === 'V') {
        const confirmation = await Alert.show({
          title: 'Atenção',
          message:
            'Verifique se há cupons de desconto cadastrado para esse produto. Caso a comissão diferenciada seja maior que o valor do produto com desconto o pagamento de seus afiliados e coprodutores será afetado.',
          confirmation: true,
          buttonOk: 'Salvar comissão',
          buttonCancel: 'Cancelar'
        });

        if (!confirmation) {
          return;
        }
      }

      affiliateService.configCustomCommission(affiliate.id, values.type, cleanValue).subscribe(
        () => {
          toast.success('Comissão diferenciada configurada com sucesso!');
          handleClose();
        },
        () => {
          toast.error(
            'Erro ao configurar comissão, verifique se o valor da comissão não é maior que o valor do produto'
          );
        },
        () => {
          setSubmitting(false);
        }
      );
    },
    [affiliate, handleClose]
  );

  return (
    <Modal
      open={open}
      destroyOnClose
      onCancel={() => handleClose()}
      title='Comissão diferenciada'
      footer={null}
      width={640}
    >
      <div className='m-2' />
      <div>
        Ao habilitar esta opção a comissão do afiliado selecionado irá sobrepor as configurações pré-definidas no
        cadastro do seu produto.
      </div>
      <div className='m-2' />

      {open && affiliate && (
        <Formik
          key={'form-commision-' + affiliate.id}
          initialValues={InitialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {() => (
            <Form>
              <FormCommision affiliate={affiliate} handleClose={handleClose} />
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default memo(CustomCommissionForm);
